import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CONTACT_CAPTCHA_URL, CONTACT_SUBMIT_URL, TELEGRAM_URL, WHATSAPP_URL } from '../../../constants/contact';

type FormState = 'idle' | 'submitting' | 'success' | 'error';

type ContactLine = string | { text: string; href: string };

type SubmitResponse = {
  ok?: boolean;
  error?: string;
  message?: string;
};

export default function ContactFormSection() {
  const { t } = useTranslation();

  const INQUIRY_TYPES = [
    t('contact.inquiry.0'), t('contact.inquiry.1'), t('contact.inquiry.2'),
    t('contact.inquiry.3'), t('contact.inquiry.4'), t('contact.inquiry.5'), t('contact.inquiry.6'), t('contact.inquiry.7'),
  ];
  const CONTACT_INFO: { icon: string; title: string; lines: ContactLine[]; color: string }[] = [
    { icon: 'ri-map-pin-2-line', title: t('contact.info.address'), lines: ['160 Robinson Road,', '#14-04 SBF Center,', 'Singapore 068914'], color: '#FF2D78' },
    {
      icon: 'ri-mail-line',
      title: t('contact.info.email'),
      lines: [t('contact.info.email.partner'), t('contact.info.email.market')],
      color: '#FF8C42',
    },
    {
      icon: 'ri-phone-line',
      title: t('contact.info.phone'),
      lines: [
        { text: t('contact.info.telegram'), href: TELEGRAM_URL },
        { text: t('contact.info.whatsapp'), href: WHATSAPP_URL },
      ],
      color: '#FF2D78',
    },
    { icon: 'ri-time-line', title: t('contact.info.hours'), lines: [t('contact.info.hours.line1'), t('contact.info.hours.line2')], color: '#FF8C42' },
  ];
  const [formState, setFormState] = useState<FormState>('idle');
  const [charCount, setCharCount] = useState(0);
  const [formError, setFormError] = useState('');
  const [captchaKey, setCaptchaKey] = useState(() => Date.now());

  const refreshCaptcha = useCallback(() => {
    setCaptchaKey(Date.now());
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) { setFormError(t('contact.form.error.maxChars')); return; }
    setFormError('');
    setFormState('submitting');
    const data = new URLSearchParams();
    new FormData(form).forEach((val, key) => { data.append(key, val as string); });
    try {
      const res = await fetch(CONTACT_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
        credentials: 'same-origin',
      });
      let body: SubmitResponse = {};
      try {
        body = await res.json() as SubmitResponse;
      } catch {
        body = {};
      }
      if (res.ok && body.ok) {
        setFormState('success');
        refreshCaptcha();
        return;
      }
      if (body.error === 'captcha_invalid' || body.error === 'captcha_required') {
        setFormError(t('contact.form.error.captcha'));
      }
      setFormState('error');
      refreshCaptcha();
    } catch {
      setFormState('error');
      refreshCaptcha();
    }
  };

  const renderLine = (line: ContactLine, j: number) => {
    if (typeof line === 'string') {
      return (
        <div key={j} className="text-sm text-gray-600 leading-relaxed">
          {line}
        </div>
      );
    }
    return (
      <a
        key={j}
        href={line.href}
        target="_blank"
        rel="noopener noreferrer"
        className="block text-sm text-gray-600 leading-relaxed hover:text-primary transition-colors"
      >
        {line.text}
      </a>
    );
  };

  return (
    <section className="bg-light-section py-20">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-10">
          <div className="bg-white border border-primary/10 rounded-2xl p-8 md:p-10">
            {formState === 'success' ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[480px] text-center gap-6">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-primary/10 border border-primary/20">
                  <i className="ri-checkbox-circle-line text-4xl text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-gray-900 mb-3">{t('contact.form.success.title')}</h3>
                  <p className="text-gray-500 leading-relaxed max-w-sm">
                    {t('contact.form.success.desc1')} <strong className="text-primary">{t('contact.form.success.descStrong')}</strong> {t('contact.form.success.desc2')}
                    {t('contact.form.success.desc3')} <span className="text-gray-700">info@geelx.com</span> {t('contact.form.success.desc4')}
                  </p>
                </div>
                <button onClick={() => setFormState('idle')} className="mt-2 px-6 py-2.5 bg-primary/10 border border-primary/25 text-primary text-sm font-medium rounded-lg hover:bg-primary/20 transition-all cursor-pointer whitespace-nowrap">
                  {t('contact.form.submitAgain')}
                </button>
              </div>
            ) : (
              <>
                <div className="mb-8">
                  <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">{t('contact.form.title')}</h2>
                  <p className="text-gray-400 text-sm">{t('contact.form.subtitle')}</p>
                </div>
                <form id="contact-inquiry-form" onSubmit={handleSubmit} className="space-y-5">
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">{t('contact.form.name')} <span className="text-primary">*</span></label>
                      <input type="text" name="name" required placeholder={t('contact.form.name.placeholder')} className="w-full bg-light-alt border border-primary/15 text-gray-700 text-sm px-4 py-2.5 rounded-lg focus:outline-none focus:border-primary/40 placeholder-gray-300 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">{t('contact.form.company')}</label>
                      <input type="text" name="company" placeholder={t('contact.form.company.placeholder')} className="w-full bg-light-alt border border-primary/15 text-gray-700 text-sm px-4 py-2.5 rounded-lg focus:outline-none focus:border-primary/40 placeholder-gray-300 transition-colors" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">{t('contact.form.email')} <span className="text-primary">*</span></label>
                      <input type="email" name="email" required placeholder="your@email.com" className="w-full bg-light-alt border border-primary/15 text-gray-700 text-sm px-4 py-2.5 rounded-lg focus:outline-none focus:border-primary/40 placeholder-gray-300 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">{t('contact.form.phone')}</label>
                      <input type="tel" name="phone" placeholder="+65 xxxx xxxx" className="w-full bg-light-alt border border-primary/15 text-gray-700 text-sm px-4 py-2.5 rounded-lg focus:outline-none focus:border-primary/40 placeholder-gray-300 transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">{t('contact.form.inquiryType')} <span className="text-primary">*</span></label>
                    <select name="inquiry_type" required defaultValue="" className="w-full bg-light-alt border border-primary/15 text-gray-700 text-sm px-4 py-2.5 rounded-lg focus:outline-none focus:border-primary/40 transition-colors cursor-pointer appearance-none" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'%239ca3af\'%3E%3Cpath d=\'M7 10l5 5 5-5z\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 14px center' }}>
                      <option value="" disabled>{t('contact.form.inquiryType.placeholder')}</option>
                      {INQUIRY_TYPES.map((type) => (<option key={type} value={type}>{type}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">{t('contact.form.budget')}</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {['< $50K', '$50K – $200K', '$200K – $500K', '> $500K'].map((b) => (
                        <label key={b} className="flex items-center gap-2 cursor-pointer group">
                          <input type="radio" name="budget" value={b} className="hidden peer" />
                          <span className="w-full text-center text-xs px-3 py-2 rounded-lg border border-primary/15 text-gray-400 peer-checked:border-primary/50 peer-checked:text-primary peer-checked:bg-primary/8 group-hover:border-primary/30 group-hover:text-gray-600 transition-all whitespace-nowrap">
                            {b}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">{t('contact.form.message')} <span className="text-primary">*</span></label>
                    <textarea name="message" required rows={5} maxLength={500} placeholder={t('contact.form.message.placeholder')} onChange={(e) => setCharCount(e.target.value.length)} className="w-full bg-light-alt border border-primary/15 text-gray-700 text-sm px-4 py-3 rounded-lg focus:outline-none focus:border-primary/40 placeholder-gray-300 transition-colors resize-none" />
                    <div className="flex items-center justify-between mt-1">
                      {formError ? <span className="text-xs text-red-400">{formError}</span> : <span className="text-xs text-gray-300">{t('contact.form.message.hint')}</span>}
                      <span className={`text-xs ${charCount > 450 ? 'text-accent' : 'text-gray-300'}`}>{charCount}/500</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-400 mb-1.5 uppercase tracking-wide">{t('contact.form.captcha')} <span className="text-primary">*</span></label>
                    <div className="flex items-center gap-3">
                      <input
                        type="text"
                        name="captcha"
                        required
                        autoComplete="off"
                        maxLength={6}
                        placeholder={t('contact.form.captcha.placeholder')}
                        className="flex-1 bg-light-alt border border-primary/15 text-gray-700 text-sm px-4 py-2.5 rounded-lg focus:outline-none focus:border-primary/40 placeholder-gray-300 transition-colors uppercase tracking-widest"
                      />
                      <button
                        type="button"
                        onClick={refreshCaptcha}
                        className="flex-shrink-0 rounded-lg border border-primary/15 overflow-hidden bg-light-alt hover:border-primary/30 transition-colors cursor-pointer"
                        aria-label={t('contact.form.captcha.refresh')}
                      >
                        <img
                          src={`${CONTACT_CAPTCHA_URL}?t=${captchaKey}`}
                          alt={t('contact.form.captcha')}
                          width={120}
                          height={44}
                          className="block"
                        />
                      </button>
                      <button
                        type="button"
                        onClick={refreshCaptcha}
                        className="text-xs text-gray-400 hover:text-primary transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {t('contact.form.captcha.refresh')}
                      </button>
                    </div>
                  </div>
                  {formState === 'error' && (
                    <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
                      <i className="ri-error-warning-line text-red-400 text-sm" />
                      <span className="text-red-500 text-sm">{t('contact.form.error.submitFailed')}</span>
                    </div>
                  )}
                  <button type="submit" disabled={formState === 'submitting'} className="w-full flex items-center justify-center gap-3 py-3.5 bg-primary text-white font-bold text-sm rounded-lg hover:bg-primary-dark transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
                    {formState === 'submitting' ? (<><i className="ri-loader-4-line animate-spin text-base" />{t('contact.form.submitting')}</>) : (<><i className="ri-send-plane-fill text-base" />{t('contact.form.submit')}</>)}
                  </button>
                  <p className="text-center text-xs text-gray-300">
                    {t('contact.form.agreePrefix')}
                    <a href="#" rel="nofollow" className="text-gray-400 hover:text-primary transition-colors mx-1">{t('footer.privacy')}</a>
                    {t('contact.form.and')}
                    <a href="#" rel="nofollow" className="text-gray-400 hover:text-primary transition-colors mx-1">{t('footer.terms')}</a>
                  </p>
                </form>
              </>
            )}
          </div>

          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              {CONTACT_INFO.map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-white border border-primary/10 rounded-xl hover:border-primary/25 transition-all duration-200">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${item.color}12`, border: `1px solid ${item.color}20` }}>
                    <i className={`${item.icon} text-base`} style={{ color: item.color }} />
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">{item.title}</div>
                    {item.lines.map((line, j) => renderLine(line, j))}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-5 bg-white border border-primary/10 rounded-xl">
              <p className="text-xs text-gray-400 uppercase tracking-wide mb-4">{t('contact.social')}</p>
              <div className="flex flex-wrap gap-3">
                {[
                  { icon: 'ri-twitter-x-line', label: 'Twitter / X', href: '#' },
                  { icon: 'ri-linkedin-line', label: 'LinkedIn', href: '#' },
                  { icon: 'ri-telegram-line', label: 'Telegram', href: TELEGRAM_URL },
                  { icon: 'ri-wechat-line', label: t('contact.social.wechat'), href: '#' },
                ].map((s, i) => (
                  <a key={i} href={s.href} target={s.href.startsWith('http') ? '_blank' : undefined} rel={s.href.startsWith('http') ? 'noopener noreferrer' : 'nofollow'} className="flex items-center gap-2 px-3 py-2 bg-light-alt border border-primary/10 rounded-lg text-gray-400 hover:text-primary hover:border-primary/25 transition-all duration-200 cursor-pointer">
                    <div className="w-4 h-4 flex items-center justify-center"><i className={`${s.icon} text-sm`} /></div>
                    <span className="text-xs">{s.label}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
